import React from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import GeneralError from './pages/errors/general-error';
import MaintenanceError from './pages/errors/maintenance-error';
import NotFoundError from './pages/errors/not-found-error';
import UnauthorisedError from './pages/errors/unauthorised-error.tsx';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { IconAlertTriangle } from '@tabler/icons-react';
import { RoleGuard } from '@/auth/role-guard';
import { Role } from '@/auth/constants';
import { logger } from '@/lib/logger';
import { ErrorBoundary } from '@/components/error-boundary';
import { DeploymentErrorBoundary } from '@/components/deployment-error-boundary';

// Helper function for lazy loading with chunk error recovery
const lazyWithRetry = (importFn: () => Promise<any>) => {
  return async () => {
    const maxRetries = 3;
    let lastError: Error | undefined;
    
    for (let i = 0; i < maxRetries; i++) {
      try {
        return await importFn();
      } catch (error: any) {
        lastError = error;
        console.warn(`Module load attempt ${i + 1}/${maxRetries} failed:`, error);
        
        // Check if it's a chunk loading error
        if (error?.message?.includes('Failed to fetch dynamically imported module') ||
            error?.message?.includes('Loading chunk') ||
            error?.message?.includes('ChunkLoadError') ||
            error?.message?.includes('Importing a module script failed') ||
            error?.name === 'ChunkLoadError') {
          
          // Wait before retry (exponential backoff)
          if (i < maxRetries - 1) {
            await new Promise(resolve => setTimeout(resolve, Math.pow(2, i) * 1000));
            continue;
          }
          
          // Last attempt failed - reload the page
          logger.warn('All module load attempts failed, reloading page');
          
          // Show user-friendly message before reload
          const shouldReload = window.confirm(
            'Aplikacija se ažurira na najnoviju verziju. Stranica će biti ponovno učitana.\n\nKliknite OK da nastavite.'
          );
          
          if (shouldReload) {
            window.location.reload();
          }
          
          // Return a loading component while waiting for reload
          return { 
            Component: () => (
              <div className="min-h-screen flex items-center justify-center p-4">
                <Card className="w-full max-w-md">
                  <CardContent className="p-6">
                    <div className="flex flex-col items-center justify-center gap-4 py-8 text-center">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
                      <h3 className="text-lg font-medium">Ažuriranje aplikacije...</h3>
                      <p className="text-sm text-muted-foreground">
                        Molimo sačekajte dok se aplikacija učita.
                      </p>
                      <Button 
                        onClick={() => window.location.reload()}
                        variant="outline"
                        className="mt-2"
                      >
                        Osveži stranicu
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </div>
            ) 
          };
        }
        
        // For non-chunk errors, retry without delay
        if (i < maxRetries - 1) {
          await new Promise(resolve => setTimeout(resolve, 500));
        }
      }
    }
    
    // If we get here, all retries failed
    console.error('Module loading failed after all retries:', lastError);
    throw lastError || new Error('Module loading failed after all retries');
  };
};

// Custom error element for main app routes (including root redirect failures)
const MainAppErrorElement = () => {
  const ConnectionError = React.lazy(() => import('./pages/errors/connection-error'));
  return (
    <React.Suspense fallback={
      <div className="min-h-screen flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="p-6">
            <div className="flex flex-col items-center justify-center gap-4 py-8 text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
              <h3 className="text-lg font-medium">Učitavanje...</h3>
              <p className="text-sm text-muted-foreground">
                Molimo sačekajte dok se aplikacija učita.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    }>
      <ConnectionError />
    </React.Suspense>
  );
};

// Custom error element for organization routes
const OrganizationErrorElement = () => (
  <Card className="mt-4 mx-4">
    <CardContent className="p-6">
      <div className="flex flex-col items-center justify-center gap-4 py-8 text-center">
        <IconAlertTriangle className="h-12 w-12 text-amber-500" />
        <h3 className="text-lg font-medium">An error occurred</h3>
        <p className="text-sm text-muted-foreground">
          There was a problem loading this content. Please try again or go back.
        </p>
        <div className="flex gap-2">
          <Button 
            onClick={() => window.location.reload()}
            variant="outline"
          >
            Try again
          </Button>
          <Button
            onClick={() => window.history.back()}
          >
            Go back
          </Button>
        </div>
      </div>
    </CardContent>
  </Card>
);

// Custom error element for reports routes with better UX
const ReportsErrorElement = () => {
  const ConnectionError = React.lazy(() => import('./pages/errors/connection-error'));
  return (
    <React.Suspense fallback={
      <Card className="mt-4 mx-4">
        <CardContent className="p-6">
          <div className="flex flex-col items-center justify-center gap-4 py-8 text-center">
            <IconAlertTriangle className="h-12 w-12 text-amber-500" />
            <h3 className="text-lg font-medium">Učitavanje stranice za greške...</h3>
          </div>
        </CardContent>
      </Card>
    }>
      <ConnectionError minimal />
    </React.Suspense>
  );
};

// Custom error element for children routes
const ChildrenErrorElement = () => (
  <Card className="mt-4 mx-4">
    <CardContent className="p-6">
      <div className="flex flex-col items-center justify-center gap-4 py-8 text-center">
        <IconAlertTriangle className="h-12 w-12 text-amber-500" />
        <h3 className="text-lg font-medium">Problem sa učitavanjem stranice</h3>
        <p className="text-sm text-muted-foreground max-w-md">
          Došlo je do problema prilikom učitavanja ove stranice. Ovo može biti zbog ažuriranja aplikacije 
          ili problema sa mrežom.
        </p>
        <div className="flex gap-2">
          <Button 
            onClick={() => window.location.reload()}
            variant="outline"
          >
            Osveži stranicu
          </Button>
          <Button
            onClick={() => window.location.href = '/children'}
          >
            Vrati se na decu
          </Button>
        </div>
      </div>
    </CardContent>
  </Card>
);

const router = createBrowserRouter([
  // Auth routes
  {
    path: '/sign-in',
    lazy: lazyWithRetry(async () => ({
      Component: (await import('./pages/auth/sign-in')).default,
    })),
  },
  {
    path: '/sign-in-2',
    lazy: lazyWithRetry(async () => ({
      Component: (await import('./pages/auth/sign-in-2')).default,
    })),
  },
  {
    path: '/sign-up',
    lazy: async () => ({
      Component: (await import('./pages/auth/sign-up')).default,
    }),
  },
  {
    path: '/forgot-password',
    lazy: async () => ({
      Component: (await import('./pages/auth/forgot-password')).default,
    }),
  },
  {
    path: '/otp',
    lazy: async () => ({
      Component: (await import('./pages/auth/otp')).default,
    }),
  },

  // Parent-only route (separate from main app)
  {
    path: '/parent-welcome',
    lazy: lazyWithRetry(async () => {
      const ParentLanding = (await import('./pages/parent-landing')).default;
      return {
        Component: ParentLanding,
      };
    }),
    errorElement: <MainAppErrorElement />,
  },

  // Onboarding route (standalone, outside AppShell)
  {
    path: '/onboarding/setup',
    lazy: lazyWithRetry(async () => {
      const FirstTimeSetup = (await import('./pages/onboarding/first-time-setup')).default;
      return {
        Component: FirstTimeSetup,
      };
    }),
    errorElement: <MainAppErrorElement />,
  },

  // Public enrollment route (standalone, no authentication)
  {
    path: '/enroll/:slug',
    lazy: lazyWithRetry(async () => {
      const PublicEnrollment = (await import('./pages/public-enrollment')).default;
      return {
        Component: PublicEnrollment,
      };
    }),
    errorElement: <MainAppErrorElement />,
  },

  // Main routes (for non-parent-only users)
  {
    path: '/',
    lazy: lazyWithRetry(async () => {
      const AppShell = await import('./components/app-shell');
      return {
        Component: () => (
          <DeploymentErrorBoundary>
            <AppShell.default />
          </DeploymentErrorBoundary>
        ),
      };
    }),
    errorElement: <MainAppErrorElement />,
    children: [
      {
        index: true,
        lazy: lazyWithRetry(async () => {
          const RootRedirect = (await import('./pages/root-redirect')).default;
          return {
            Component: RootRedirect,
          };
        }),
        errorElement: <MainAppErrorElement />,
      },
      // Dashboard routes
      {
        path: 'dashboard',
        children: [
          {
            index: true,
            element: <Navigate to="/dashboard/overview" replace />
          },
          {
            path: 'overview',
            lazy: lazyWithRetry(async () => {
              const Dashboard = (await import('./pages/dashboard')).default;
              return {
                Component: () => (
                  <RoleGuard excludedRoles={[Role.Parent]}>
                    <Dashboard />
                  </RoleGuard>
                ),
              };
            }),
            errorElement: <MainAppErrorElement />,
          }
        ]
      },
      // Legacy chat routes - redirect to main chat
      {
        path: 'chats',
        errorElement: <OrganizationErrorElement />,
        children: [
          {
            path: 'spaces',
            element: <Navigate to="/chat" replace />,
          },
          {
            path: 'spaces/:spaceId',
            element: <Navigate to="/chat" replace />,
          },
          {
            path: 'spaces/:spaceId/members',
            element: <Navigate to="/chat" replace />,
          },
          {
            path: 'spaces/:spaceId/chat',
            element: <Navigate to="/chat" replace />,
          },
          {
            path: 'recent',
            element: <Navigate to="/chat" replace />,
          },
        ],
      },
      // Simplified chat route that relies on headers for context
      {
        path: 'chat',
        lazy: async () => {
          const Component = (await import('./pages/chats/chat-interface')).default;
          return {
            Component: () => (
              <ErrorBoundary>
                <Component />
              </ErrorBoundary>
            ),
          };
        },
        errorElement: <OrganizationErrorElement />,
      },
      // Galleries hub - browse/upload photos for kids, groups, institutions and events
      {
        path: 'galleries',
        lazy: lazyWithRetry(async () => {
          const Component = (await import('./pages/galleries')).default;
          return {
            Component: () => (
              <ErrorBoundary>
                <Component />
              </ErrorBoundary>
            ),
          };
        }),
        errorElement: <OrganizationErrorElement />,
      },
      {
        path: 'edit-group/:id',
        lazy: lazyWithRetry(async () => ({
          Component: (await import('./pages/children/edit-group')).default,
        })),
        errorElement: <OrganizationErrorElement />,
      },
      {
        path: 'child-sign-in/:childId?',
        lazy: lazyWithRetry(async () => ({
          Component: (await import('./pages/child-sign-in')).default,
        })),
        errorElement: <OrganizationErrorElement />,
      },

      {
        path: 'children/edit-profile/:id',
        lazy: lazyWithRetry(async () => ({
          Component: (await import('./pages/children/edit-profile')).default,
        })),
        errorElement: <ChildrenErrorElement />,
      },
      {
        path: 'children',
        lazy: lazyWithRetry(async () => ({
          Component: (await import('./pages/children')).default,
        })),
        errorElement: <ChildrenErrorElement />,
        children: [
          {
            path: 'groups',
            lazy: lazyWithRetry(async () => {
              const Component = (await import('./pages/children/groups')).default;
              return {
                Component: () => (
                  <DeploymentErrorBoundary>
                    <ErrorBoundary>
                      <Component />
                    </ErrorBoundary>
                  </DeploymentErrorBoundary>
                ),
              };
            }),
            errorElement: <ChildrenErrorElement />,
          },
          {
            path: 'sign-in',
            lazy: lazyWithRetry(async () => {
              const Component = (await import('./pages/children/sign-in')).default;
              return {
                Component: () => (
                  <DeploymentErrorBoundary>
                    <ErrorBoundary>
                      <Component />
                    </ErrorBoundary>
                  </DeploymentErrorBoundary>
                ),
              };
            }),
            errorElement: <ChildrenErrorElement />,
          },
          {
            path: 'profile',
            lazy: lazyWithRetry(async () => {
              const Component = (await import('./pages/children/profile')).default;
              return {
                Component: () => (
                  <DeploymentErrorBoundary>
                    <ErrorBoundary>
                      <Component />
                    </ErrorBoundary>
                  </DeploymentErrorBoundary>
                ),
              };
            }),
            errorElement: <ChildrenErrorElement />,
          },
        ],
      },
      {
        path: 'users',
        lazy: async () => ({
          Component: (await import('./pages/users')).default,
        }),
        children: [
          {
            path: 'employees',
            lazy: async () => {
              const Employees = (await import('./pages/users/employees')).default;
              return {
                Component: () => (
                  <RoleGuard>
                    <Employees />
                  </RoleGuard>
                ),
              };
            },
          },
          {
            path: 'parents',
            lazy: async () => ({
              Component: (await import('./pages/users/parents')).default,
            }),
          },

        ],
      },
      {
        path: 'tasks',
        lazy: async () => ({
          Component: (await import('@/pages/tasks')).default,
        }),
        children: [
          {
            path: 'list',
            lazy: async () => ({
              Component: (await import('@/pages/tasks/list')).default,
            }),
          },
          {
            path: 'board',
            lazy: async () => ({
              Component: (await import('@/pages/tasks/board')).default,
            }),
          },
        ],
      },
      
      // Events routes - accessible by Admin, Teacher, Employee, Supervisor
      {
        path: 'events',
        lazy: async () => {
          const Events = (await import('@/pages/events')).default;
          return {
            Component: () => (
              <RoleGuard>
                <Events />
              </RoleGuard>
            ),
          };
        },
        children: [
          {
            path: 'list',
            lazy: async () => {
              const EventsList = (await import('@/pages/events/list')).default;
              return {
                Component: () => (
                  <RoleGuard>
                    <EventsList />
                  </RoleGuard>
                ),
              };
            },
          },
          {
            path: 'calendar',
            lazy: async () => {
              const EventsCalendar = (await import('@/pages/events/calendar')).default;
              return {
                Component: () => (
                  <RoleGuard>
                    <EventsCalendar />
                  </RoleGuard>
                ),
              };
            },
          },
          {
            path: 'create',
            lazy: async () => {
              const CreateEvent = (await import('@/pages/events/create')).default;
              return {
                Component: () => (
                  <RoleGuard>
                    <CreateEvent />
                  </RoleGuard>
                ),
              };
            },
          },
          {
            path: 'edit/:id',
            lazy: async () => {
              const EditEvent = (await import('@/pages/events/edit')).default;
              return {
                Component: () => (
                  <RoleGuard>
                    <EditEvent />
                  </RoleGuard>
                ),
              };
            },
          },
          {
            path: 'detail/:id',
            lazy: async () => {
              const EditEvent = (await import('@/pages/events/edit')).default;
              return {
                Component: () => (
                  <RoleGuard>
                    <EditEvent />
                  </RoleGuard>
                ),
              };
            },
          },
          {
            path: 'groups',
            lazy: async () => {
              const EventGroups = (await import('@/pages/events/groups')).default;
              return {
                Component: () => (
                  <RoleGuard>
                    <EventGroups />
                  </RoleGuard>
                ),
              };
            },
          },
          {
            path: 'groups/edit/:id',
            lazy: async () => {
              const EditEventGroup = (await import('@/pages/events/groups/edit-group')).default;
              return {
                Component: () => (
                  <RoleGuard>
                    <EditEventGroup />
                  </RoleGuard>
                ),
              };
            },
          },
          {
            path: 'programs',
            lazy: async () => {
              const EventPrograms = (await import('@/pages/events/programs')).default;
              return {
                Component: () => (
                  <RoleGuard>
                    <EventPrograms />
                  </RoleGuard>
                ),
              };
            },
          },
          {
            path: 'programs/edit/:id',
            lazy: async () => {
              const EditEventProgram = (await import('@/pages/events/programs/edit-program')).default;
              return {
                Component: () => (
                  <RoleGuard>
                    <EditEventProgram />
                  </RoleGuard>
                ),
              };
            },
          },
          {
            path: 'food-menu',
            lazy: async () => {
              const EventFoodMenu = (await import('@/pages/events/food-menu')).default;
              return {
                Component: () => (
                  <RoleGuard>
                    <EventFoodMenu />
                  </RoleGuard>
                ),
              };
            },
          },
          {
            path: 'food-menu/edit/:id',
            lazy: async () => {
              const EditFoodMenu = (await import('@/pages/events/food-menu/edit.tsx')).default;
              return {
                Component: () => (
                  <RoleGuard>
                    <EditFoodMenu />
                  </RoleGuard>
                ),
              };
            },
          },
          {
            path: 'food-menu/planning/:id',
            lazy: async () => {
              const FoodPlanningDashboard = (await import('@/pages/events/food-menu/food-planning.tsx')).default;
              return {
                Component: () => (
                  <RoleGuard>
                    <FoodPlanningDashboard />
                  </RoleGuard>
                ),
              };
            },
          },
        ],
      },
      {
        path: 'reports',
        lazy: lazyWithRetry(async () => ({
          Component: (await import('./pages/reports')).default,
        })),
        errorElement: <ReportsErrorElement />,
        children: [
          {
            path: 'daily',
            lazy: lazyWithRetry(async () => ({
              Component: (await import('./pages/reports/daily')).default,
            })),
          },
          {
            path: 'weekly',
            lazy: lazyWithRetry(async () => ({
              Component: (await import('./pages/reports/weekly')).default,
            })),
          },
          {
            path: 'monthly',
            lazy: lazyWithRetry(async () => ({
              Component: (await import('./pages/reports/monthly')).default,
            })),
          },
          {
            path: 'presence',
            lazy: lazyWithRetry(async () => ({
              Component: (await import('./pages/reports/presence')).default,
            })),
          },
        ],
      },

      {
        path: 'organization',
        lazy: async () => ({
          Component: (await import('@/pages/organization')).default,
        }),
        errorElement: <OrganizationErrorElement />,
        children: [
          {
            path: 'contracts/my-contracts',
            lazy: async () => {
              const MyContracts = (await import('@/pages/organization/contracts-my-contracts')).default;
              return {
                Component: () => (
                  <RoleGuard>
                    <MyContracts />
                  </RoleGuard>
                ),
              };
            },
          },
          {
            path: 'contracts/templates',
            lazy: async () => {
              const TemplateLibrary = (await import('@/pages/organization/contracts-templates')).default;
              return {
                Component: () => (
                  <RoleGuard>
                    <TemplateLibrary />
                  </RoleGuard>
                ),
              };
            },
          },
          {
            path: 'contracts/edit/:id',
            lazy: async () => {
              const EditTemplate = (await import('@/pages/organization/contracts-edit-template')).default;
              return {
                Component: () => (
                  <RoleGuard>
                    <EditTemplate />
                  </RoleGuard>
                ),
              };
            },
          },
          {
            path: 'contracts',
            lazy: async () => {
              const OrganizationContracts = (await import('@/pages/organization/contracts-my-contracts')).default;
              return {
                Component: () => (
                  <RoleGuard>
                    <OrganizationContracts />
                  </RoleGuard>
                ),
              };
            },
          },
          {
            path: 'institutions',
            lazy: async () => {
              const OrganizationInstitutions = (await import('@/pages/organization/institutions')).default;
              return {
                Component: () => (
                  <RoleGuard>
                    <OrganizationInstitutions />
                  </RoleGuard>
                ),
              };
            },
          },
          {
            path: 'institutions/detail',
            lazy: async () => {
              const InstitutionDetail = (await import('@/pages/organization/institutions/[id]')).default;
              return {
                Component: () => (
                  <RoleGuard>
                    <InstitutionDetail />
                  </RoleGuard>
                ),
              };
            },
            errorElement: <OrganizationErrorElement />,
          },
          {
            path: 'institutions/:institutionId/edit',
            lazy: async () => {
              const EditInstitution = (await import('@/pages/organization/institutions/edit-institution')).default;
              return {
                Component: () => (
                  <RoleGuard>
                    <EditInstitution />
                  </RoleGuard>
                ),
              };
            },
            errorElement: <OrganizationErrorElement />,
          },
          {
            path: 'institutions/:institutionId/enrollment-settings',
            lazy: async () => {
              const EnrollmentSettings = (await import('@/pages/organization/enrollment-settings')).default;
              return {
                Component: () => (
                  <RoleGuard>
                    <EnrollmentSettings />
                  </RoleGuard>
                ),
              };
            },
            errorElement: <OrganizationErrorElement />,
          },
          {
            path: 'institutions/:institutionId/enrollment-requests',
            lazy: async () => {
              const EnrollmentRequests = (await import('@/pages/organization/enrollment-requests')).default;
              return {
                Component: () => (
                  <RoleGuard>
                    <EnrollmentRequests />
                  </RoleGuard>
                ),
              };
            },
            errorElement: <OrganizationErrorElement />,
          },
          {
            path: 'users',
            lazy: async () => {
              const OrganizationUsers = (await import('@/pages/organization/users')).default;
              return {
                Component: () => (
                  <RoleGuard>
                    <OrganizationUsers />
                  </RoleGuard>
                ),
              };
            },
            errorElement: <OrganizationErrorElement />,
          },
          {
            path: 'settings',
            lazy: async () => {
              const OrganizationSettings = (await import('@/pages/organization/settings')).default;
              return {
                Component: () => (
                  <RoleGuard>
                    <OrganizationSettings />
                  </RoleGuard>
                ),
              };
            },
          },
        ],
      },
      {
        path: 'extra-components',
        lazy: async () => ({
          Component: (await import('@/pages/extra-components')).default,
        }),
      },
      {
        path: 'settings',
        lazy: async () => ({
          Component: (await import('./pages/settings')).default,
        }),
        errorElement: <GeneralError />,
        children: [
          {
            index: true,
            lazy: async () => ({
              Component: (await import('./pages/settings/profile')).default,
            }),
          },


          {
            path: 'notifications',
            lazy: async () => ({
              Component: (await import('./pages/settings/notifications'))
                .default,
            }),
          },


        ],
      },
      {
        path: 'dashboard/calendar',
        lazy: async () => {
          const Calendar = (await import('./pages/dashboard/calendar')).default;
          return {
            Component: () => (
              <RoleGuard excludedRoles={[Role.Parent]}>
                <Calendar />
              </RoleGuard>
            ),
          };
        },
      },
      {
        path: 'dashboard/overview',
        lazy: async () => {
          const Dashboard = (await import('./pages/dashboard')).default;
          return {
            Component: () => (
              <RoleGuard excludedRoles={[Role.Parent]}>
                <Dashboard />
              </RoleGuard>
            ),
          };
        },
      },
      {
        path: 'finances',
        lazy: async () => {
          const Finances = (await import('./pages/finances')).default;
          return {
            Component: () => (
              <RoleGuard allowedRoles={[Role.Admin, Role.Finance]}>
                <Finances />
              </RoleGuard>
            ),
          };
        },
        children: [
          {
            path: 'invoices',
            lazy: async () => {
              const Invoices = (await import('./pages/finances/invoices')).default;
              return {
                Component: () => (
                  <RoleGuard allowedRoles={[Role.Admin, Role.Finance]}>
                    <Invoices />
                  </RoleGuard>
                ),
              };
            },
          },
          {
            path: 'invoices/:id',
            lazy: async () => {
              const InvoiceDetails = (await import('./pages/finances/invoice-details')).default;
              return {
                Component: () => (
                  <RoleGuard allowedRoles={[Role.Admin, Role.Finance]}>
                    <InvoiceDetails />
                  </RoleGuard>
                ),
              };
            },
          },
          {
            path: 'packages',
            lazy: async () => {
              const Packages = (await import('./pages/finances/packages')).default;
              return {
                Component: () => (
                  <RoleGuard allowedRoles={[Role.Admin, Role.Finance]}>
                    <Packages />
                  </RoleGuard>
                ),
              };
            },
          },
          {
            path: 'settings',
            lazy: async () => {
              const FinancesSettings = (await import('./pages/finances/settings')).default;
              return {
                Component: () => (
                  <RoleGuard allowedRoles={[Role.Admin, Role.Finance]}>
                    <FinancesSettings />
                  </RoleGuard>
                ),
              };
            },
          },
          {
            path: 'expenses',
            lazy: async () => {
              const Expenses = (await import('./pages/finances/expenses')).default;
              return {
                Component: () => (
                  <RoleGuard allowedRoles={[Role.Admin, Role.Finance, Role.Supervisor, Role.Employee, Role.Teacher]}>
                    <Expenses />
                  </RoleGuard>
                ),
              };
            },
          },
        ],
      },
      
      // Support Chat (previously Help Center)
      {
        path: 'help-center',
        lazy: lazyWithRetry(async () => ({
          Component: (await import('./pages/support-chat')).default,
        })),
        errorElement: <MainAppErrorElement />,
      },
      {
        path: 'support-chat',
        lazy: lazyWithRetry(async () => ({
          Component: (await import('./pages/support-chat')).default,
        })),
        errorElement: <MainAppErrorElement />,
      },
    ],
  },
  // Error routes
  { path: '/500', Component: GeneralError },
  { path: '/404', Component: NotFoundError },
  { path: '/503', Component: MaintenanceError },
  { path: '/401', Component: UnauthorisedError },

  // Fallback 404 route
  { path: '*', Component: NotFoundError },
]);

export default router;
